import React, { useMemo, useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import { Checkbox } from "./Checkbox";
import { PeriodPicker } from "./PeriodPicker";
import { useClickOutside } from "./useClickOutside";
import { SCHOOL_NAMES, TEACHERS_BY_SCHOOL, GRADES } from "../data/data";

export interface Filters {
  schools: string[];
  teachers: string[];
  groups: string[];
  grades: number[];
  categories: string[];
  dateRange: string;
  periodLabel: string;
}

export type FilterKey = "site" | "teacher" | "grade" | "category" | "period";

function Pill({
  label,
  value,
  open,
  active,
  disabled,
  onClick,
  title,
}: {
  label?: string;
  value: string;
  open?: boolean;
  active?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  title?: string;
}) {
  const borderActive = open || active;
  return (
    <button
      onClick={disabled ? undefined : onClick}
      title={title}
      className={`flex items-center gap-2 whitespace-nowrap rounded-[6px] border px-4 py-[9px] text-sm font-semibold transition-colors duration-100 ${
        disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
      } ${
        borderActive
          ? "border-[#28543D] bg-[#FBF8F1]"
          : "border-[#D4D3C5] bg-[#FBF8F1]"
      } text-[#294B39]`}
    >
      {label && <span className="font-semibold text-[#687368]">{label}</span>}
      <span>{value}</span>
      <ChevronDown
        size={15}
        strokeWidth={2.4}
        className={`text-[#687368] transition-transform duration-150 ${open ? "rotate-180" : ""}`}
      />
    </button>
  );
}

function Panel({
  children,
  width = 280,
}: {
  children: React.ReactNode;
  width?: number;
}) {
  return (
    <div
      className="absolute left-0 top-[calc(100%+8px)] z-50 rounded-[11px] border border-[#DED8CC] bg-[#FBF8F1] p-3.5 shadow-[0_12px_40px_rgba(18,18,18,0.16)]"
      style={{ width }}
    >
      {children}
    </div>
  );
}

function ApplyButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="mt-3 w-full cursor-pointer rounded-[6px] border-none bg-[#28543D] hover:bg-[#204833] py-2.5 text-sm font-bold text-white"
    >
      Apply
    </button>
  );
}

function TextLink({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="cursor-pointer border-none bg-transparent p-0 text-[13px] font-bold text-[#28543D]"
    >
      {children}
    </button>
  );
}

function SearchRow({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <div className="mb-2.5 flex items-center gap-2 rounded-[6px] border border-[#D4D3C5] px-2.5 py-2 focus-within:border-[#28543D]">
      <Search size={15} className="text-[#687368]" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full border-none bg-transparent text-sm text-[#294B39] outline-none"
      />
    </div>
  );
}

function CheckItem({
  label,
  checked,
  onToggle,
}: {
  label: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <div
      onClick={onToggle}
      className="flex cursor-pointer items-center gap-2.5 rounded-[6px] p-2 text-sm font-medium hover:bg-[#28543D]/5"
    >
      <Checkbox checked={checked} />
      <span>{label}</span>
    </div>
  );
}

// A pill + dropdown panel with click-outside handling.
function FilterDropdown({
  id,
  openId,
  setOpenId,
  pill,
  children,
  width,
}: {
  id: string;
  openId: string | null;
  setOpenId: (id: string | null) => void;
  pill: (toggle: () => void, open: boolean) => React.ReactNode;
  children: React.ReactNode;
  width?: number;
}) {
  const ref = useClickOutside<HTMLDivElement>(() => {
    if (openId === id) setOpenId(null);
  });
  const open = openId === id;
  return (
    <div className="relative" ref={ref}>
      {pill(() => setOpenId(open ? null : id), open)}
      {open && <Panel width={width}>{children}</Panel>}
    </div>
  );
}

export function FilterToolbar({
  filters,
  onChange,
  categoryOptions,
  breadcrumb,
  hiddenFilters = [],
}: {
  filters: Filters;
  onChange: (next: Filters) => void;
  categoryOptions: string[];
  breadcrumb?: React.ReactNode;
  hiddenFilters?: FilterKey[];
}) {
  const [openId, setOpenId] = useState<string | null>(null);

  const [schoolDraft, setSchoolDraft] = useState<string[]>(filters.schools);
  const [teacherDraft, setTeacherDraft] = useState<string[]>(filters.teachers);
  const [gradeDraft, setGradeDraft] = useState<number[]>(filters.grades);
  const [categoryDraft, setCategoryDraft] = useState<string[]>(
    filters.categories,
  );
  const [schoolSearch, setSchoolSearch] = useState("");
  const [teacherSearch, setTeacherSearch] = useState("");

  const teacherEnabled = true;
  const availableTeachers = useMemo(() => {
    const src = filters.schools.length ? filters.schools : [...SCHOOL_NAMES];
    const set = new Set<string>();
    src.forEach((s) =>
      (TEACHERS_BY_SCHOOL[s] || []).forEach((t) => set.add(t)),
    );
    return Array.from(set);
  }, [filters.schools]);

  const openWithDraft = (id: string) => {
    if (id === "school") setSchoolDraft(filters.schools);
    if (id === "teacher") setTeacherDraft(filters.teachers);
    if (id === "grade") setGradeDraft(filters.grades);
    if (id === "category") setCategoryDraft(filters.categories);
    setOpenId(id);
  };
  const close = () => setOpenId(null);

  const siteLabel =
    filters.schools.length === 0
      ? "All sites"
      : filters.schools.length === 1
        ? filters.schools[0]
        : `${filters.schools.length} sites`;
  const teacherLabel =
    filters.teachers.length === 0
      ? "All teachers"
      : filters.teachers.length === 1
        ? filters.teachers[0]
        : `${filters.teachers.length} teachers`;
  const gradeLabel =
    filters.grades.length === 3
      ? "Grades 6-8"
      : filters.grades.length === 0
        ? "No grades"
        : "Grade " + [...filters.grades].sort().join(", ");
  const categoryLabel =
    filters.categories.length === 0
      ? "All categories"
      : filters.categories.length === 1
        ? filters.categories[0]
        : `${filters.categories.length} categories`;

  const filteredSchools = SCHOOL_NAMES.filter((s) =>
    s.toLowerCase().includes(schoolSearch.toLowerCase()),
  );
  const filteredTeachers = availableTeachers.filter((t) =>
    t.toLowerCase().includes(teacherSearch.toLowerCase()),
  );

  const summaryParts = [
    siteLabel,
    teacherEnabled ? teacherLabel : "All teachers",
    gradeLabel,
    categoryLabel,
    filters.periodLabel,
  ];

  const toggleFrom = <T,>(arr: T[], v: T): T[] =>
    arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];

  return (
    <div className="flex flex-col gap-3">
      {breadcrumb ?? (
        <div className="flex flex-wrap items-center gap-2 text-[13px] text-[#687368]">
          <span className="font-bold">Showing:</span>
          {summaryParts.map((p, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span className="opacity-40">|</span>}
              <span>{p}</span>
            </React.Fragment>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2.5">
        {/* Site */}
        {!hiddenFilters.includes("site") && (
          <FilterDropdown
            id="school"
            openId={openId}
            setOpenId={(id) => (id ? openWithDraft(id) : close())}
            pill={(toggle, open) => (
              <Pill
                label="Site:"
                value={siteLabel}
                open={open}
                active={filters.schools.length > 0}
                onClick={toggle}
              />
            )}
          >
            <SearchRow
              value={schoolSearch}
              onChange={setSchoolSearch}
              placeholder="Search sites"
            />
            <div className="flex gap-4 px-1.5 pb-2 pt-0.5">
              <TextLink onClick={() => setSchoolDraft([...SCHOOL_NAMES])}>
                Select All
              </TextLink>
              <TextLink onClick={() => setSchoolDraft([])}>Clear All</TextLink>
            </div>
            <div className="max-h-[220px] overflow-y-auto">
              {filteredSchools.map((s) => (
                <CheckItem
                  key={s}
                  label={s}
                  checked={schoolDraft.includes(s)}
                  onToggle={() => setSchoolDraft((d) => toggleFrom(d, s))}
                />
              ))}
            </div>
            <ApplyButton
              onClick={() => {
                const stillValid = filters.teachers.filter((t) =>
                  schoolDraft.some((s) =>
                    (TEACHERS_BY_SCHOOL[s] || []).includes(t),
                  ),
                );
                onChange({
                  ...filters,
                  schools: schoolDraft,
                  teachers: schoolDraft.length ? stillValid : [],
                });
                close();
              }}
            />
          </FilterDropdown>
        )}

        {/* Teacher */}
        {!hiddenFilters.includes("teacher") && (
          <FilterDropdown
            id="teacher"
            openId={openId}
            setOpenId={(id) => (id ? openWithDraft(id) : close())}
            pill={(toggle, open) => (
              <Pill
                label="Teacher:"
                value={teacherLabel}
                open={open}
                active={filters.teachers.length > 0}
                onClick={toggle}
              />
            )}
          >
            <SearchRow
              value={teacherSearch}
              onChange={setTeacherSearch}
              placeholder="Search teachers"
            />
            <div className="flex gap-4 px-1.5 pb-2 pt-0.5">
              <TextLink onClick={() => setTeacherDraft([...availableTeachers])}>
                Select All
              </TextLink>
              <TextLink onClick={() => setTeacherDraft([])}>Clear All</TextLink>
            </div>
            <div className="max-h-[220px] overflow-y-auto">
              {filteredTeachers.map((t) => (
                <CheckItem
                  key={t}
                  label={t}
                  checked={teacherDraft.includes(t)}
                  onToggle={() => setTeacherDraft((d) => toggleFrom(d, t))}
                />
              ))}
            </div>
            <ApplyButton
              onClick={() => {
                onChange({ ...filters, teachers: teacherDraft });
                close();
              }}
            />
          </FilterDropdown>
        )}

        {/* Grade */}
        {!hiddenFilters.includes("grade") && (
          <FilterDropdown
            id="grade"
            openId={openId}
            setOpenId={(id) => (id ? openWithDraft(id) : close())}
            width={200}
            pill={(toggle, open) => (
              <Pill
                label="Grade:"
                value={gradeLabel}
                open={open}
                active={filters.grades.length > 0 && filters.grades.length < 3}
                onClick={toggle}
              />
            )}
          >
            <div className="flex gap-4 px-1.5 pb-2 pt-0.5">
              <TextLink onClick={() => setGradeDraft([...GRADES])}>
                Select All
              </TextLink>
              <TextLink onClick={() => setGradeDraft([])}>Clear All</TextLink>
            </div>
            {GRADES.map((g) => (
              <CheckItem
                key={g}
                label={`Grade ${g}`}
                checked={gradeDraft.includes(g)}
                onToggle={() =>
                  setGradeDraft((d) =>
                    d.includes(g) ? d.filter((x) => x !== g) : [...d, g].sort(),
                  )
                }
              />
            ))}
            <ApplyButton
              onClick={() => {
                onChange({ ...filters, grades: [...gradeDraft].sort() });
                close();
              }}
            />
          </FilterDropdown>
        )}

        {/* Category */}
        {!hiddenFilters.includes("category") && (
          <FilterDropdown
            id="category"
            openId={openId}
            setOpenId={(id) => (id ? openWithDraft(id) : close())}
            width={220}
            pill={(toggle, open) => (
              <Pill
                label="Category:"
                value={categoryLabel}
                open={open}
                active={filters.categories.length > 0}
                onClick={toggle}
              />
            )}
          >
            <div className="flex gap-4 px-1.5 pb-2 pt-0.5">
              <TextLink onClick={() => setCategoryDraft([...categoryOptions])}>
                Select All
              </TextLink>
              <TextLink onClick={() => setCategoryDraft([])}>
                Clear All
              </TextLink>
            </div>
            {categoryOptions.map((c) => (
              <CheckItem
                key={c}
                label={c}
                checked={categoryDraft.includes(c)}
                onToggle={() => setCategoryDraft((d) => toggleFrom(d, c))}
              />
            ))}
            <ApplyButton
              onClick={() => {
                onChange({ ...filters, categories: categoryDraft });
                close();
              }}
            />
          </FilterDropdown>
        )}

        {/* Time period */}
        {!hiddenFilters.includes("period") && (
          <PeriodPicker
            label={filters.periodLabel}
            onApply={(bucket, label) => {
              onChange({ ...filters, dateRange: bucket, periodLabel: label });
            }}
            onRemove={() => {
              onChange({
                ...filters,
                dateRange: "This year",
                periodLabel: "This year",
              });
            }}
          />
        )}
      </div>
    </div>
  );
}
